import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Trash2, PlusCircle, Quote, Sparkles, ArrowLeft, X, Loader2, Check } from "lucide-react";
import { streakService } from "@/services/streakService";
import { ContextModal } from "@/components/ui/ContextModal";
import { wordBankService } from "@/services/wordBankService";
import { flashcardService } from "@/services/flashcardService";

export default function Vocabulary() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [vocab, setVocab] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [createdFlashcards, setCreatedFlashcards] = useState<Record<string, boolean>>({});
  const [creatingFlashcardId, setCreatingFlashcardId] = useState<string | null>(null);

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isDeletingBulk, setIsDeletingBulk] = useState(false);

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(t("vocabulary.confirmDeleteWords", "Tem certeza que deseja excluir as {{count}} palavras selecionadas?", { count: selectedIds.length }))) return;

    setIsDeletingBulk(true);
    console.log("[WORD_BANK_BULK_DELETE_START]", {
      count: selectedIds.length,
      selectedIds,
    });

    try {
      const deletedCount = await wordBankService.deleteMultipleWords(selectedIds);
      console.log("[WORD_BANK_BULK_DELETE_SUCCESS]", {
        count: deletedCount,
      });
      setSelectedIds([]);
      await fetchVocabulary(true);
    } catch (err: any) {
      console.error("[WORD_BANK_BULK_DELETE_ERROR]", err);
      alert("Erro ao excluir múltiplas palavras: " + (err.message || "Erro desconhecido"));
    } finally {
      setIsDeletingBulk(false);
    }
  };

  const fetchVocabulary = async (isSilent = false) => {
    try {
      if (!isSilent) setIsLoading(true);
      const list = await wordBankService.getWords();
      setVocab(list);

      try {
        const flashcards = await flashcardService.getFlashcards();
        const mapping: Record<string, boolean> = {};
        flashcards.forEach(fc => {
          if (fc.sourceWordId) {
            mapping[fc.sourceWordId] = true;
          }
        });
        setCreatedFlashcards(mapping);
      } catch (fErr) {
        console.error("Error loading flashcards mapping for word bank:", fErr);
      }
    } catch (err) {
      console.error("Error loading word bank:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchVocabulary();
  }, []);

  useEffect(() => {
    const refresh = () => {
      fetchVocabulary(true);
    };
    window.addEventListener("vocabularyUpdated", refresh);
    window.addEventListener("dashboardUpdated", refresh);
    return () => {
      window.removeEventListener("vocabularyUpdated", refresh);
      window.removeEventListener("dashboardUpdated", refresh);
    };
  }, []);

  // Modal form states
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedWord, setSelectedWord] = useState<any | null>(null);
  const [newTerm, setNewTerm] = useState("");
  const [newMeaning, setNewMeaning] = useState("");
  const [newContext, setNewContext] = useState("");
  const [newCategory, setNewCategory] = useState("Substantivo");
  const [newTagsStr, setNewTagsStr] = useState("");

  const getCategoryLabel = (cat: string) => {
    switch (cat) {
      case "Conectores": return t("vocabulary.categories.connectors", "Conectores");
      case "Substantivo": return t("vocabulary.categories.noun", "Substantivo");
      case "Verbo": return t("vocabulary.categories.verb", "Verbo");
      case "Adjetivo": return t("vocabulary.categories.adjective", "Adjetivo");
      case "Idiomatismo": return t("vocabulary.categories.idiom", "Idiomatismo");
      default: return cat;
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm(t("vocabulary.confirmDeleteWord", "Deseja realmente excluir esta palavra?"))) return;
    try {
      await wordBankService.deleteWord(id);
      fetchVocabulary(true);
    } catch (err: any) {
      console.error("Error deleting word record:", err);
      let errMsg = err.message || t("vocabulary.deleteError", "Erro ao excluir a palavra.");
      if (err.message?.includes("authenticated") || err.message?.includes("autenticado") || err.message?.includes("security") || err.message?.includes("JWT") || err.status === 401 || err.status === 403) {
        errMsg = t("vocabulary.sessionExpired", "Não foi possível salvar. Sua sessão pode ter expirado. Faça login novamente.");
      }
      alert(errMsg);
    }
  };

  const handleCreateFlashcard = async (item: any) => {
    if (creatingFlashcardId) return;
    setCreatingFlashcardId(item.id);
    try {
      await wordBankService.createFlashcardFromWord(item.id);
      setCreatedFlashcards(prev => ({ ...prev, [item.id]: true }));
      alert(t("vocabulary.flashcardCreatedSuccess", "Flashcard criado com sucesso."));
    } catch (ex: any) {
      console.error("Error creating flashcard:", ex);
      if (ex.message && ex.message.includes("já existe")) {
        alert(t("vocabulary.flashcardExists", "Este flashcard já existe."));
      } else if (ex.message?.includes("authenticated") || ex.message?.includes("autenticado") || ex.message?.includes("security") || ex.status === 401 || ex.status === 403) {
        alert(t("vocabulary.sessionExpired", "Não foi possível salvar. Sua sessão pode ter expirado. Faça login novamente."));
      } else {
        alert(t("vocabulary.createFlashcardError", "Erro ao criar flashcard: {{error}}", { error: ex.message || "Erro desconhecido." }));
      }
    } finally {
      setCreatingFlashcardId(null);
    }
  };

  const handleAddSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTerm || !newMeaning) return;
    if (isSaving) return;

    setIsSaving(true);
    try {
      await wordBankService.createWord({
        term: newTerm,
        meaning: newMeaning,
        context: newContext || t("vocabulary.defaultContext", "Exemplo de uso de {{term}}", { term: newTerm }),
        category: newCategory,
        tags: newTagsStr ? newTagsStr.split(",").map(t => t.trim()).filter(Boolean) : ["manual"]
      });

      await fetchVocabulary(true);

      setNewTerm("");
      setNewMeaning("");
      setNewContext("");
      setNewCategory("Substantivo");
      setNewTagsStr("");
      setIsAddOpen(false);
    } catch (err: any) {
      console.error("Error saving vocabulary word:", err);
      let errMsg = err.message || t("vocabulary.saveError", "Erro ao salvar a palavra no banco.");
      if (err.message?.includes("authenticated") || err.message?.includes("autenticado") || err.message?.includes("security") || err.message?.includes("JWT") || err.status === 401 || err.status === 403) {
        errMsg = t("vocabulary.sessionExpired", "Não foi possível salvar. Sua sessão pode ter expirado. Faça login novamente.");
      }
      alert(errMsg);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500 pb-12">
      <div className="flex items-center justify-between">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(-1)}
          className="text-gray-500 hover:text-gray-700 hover:bg-gray-100/50 -ml-2 rounded-lg gap-1.5 font-semibold text-xs"
        >
          <ArrowLeft className="w-4 h-4" />
          {t("common.back", "Voltar")}
        </Button>
        <Button 
          onClick={() => setIsAddOpen(true)}
          className="bg-primary hover:bg-primary/90 text-white rounded-xl shadow-md font-bold text-xs"
        >
          <PlusCircle className="w-4 h-4 mr-1.5" />
          {t("vocabulary.addWord", "Adicionar Palavra")}
        </Button>
      </div>

      <div>
        <h1 className="text-3xl font-bold tracking-tight text-gray-900">{t("sidebar.vocabulary", "Banco de Palavras")}</h1>
        <p className="text-gray-500 mt-1">{t("vocabulary.description", "Sua coleção de vocabulário avançado, idiomatismos e conectores em francês.")}</p>
      </div>

      {isLoading ? (
        <div className="py-24 text-center text-gray-400 flex flex-col items-center justify-center bg-white rounded-2xl border border-gray-100 shadow-sm animate-pulse">
          <Loader2 className="w-10 h-10 text-primary animate-spin mb-4" />
          <h4 className="font-bold text-gray-700 text-base">{t("vocabulary.loadingTitle", "Carregando banco de palavras...")}</h4>
          <p className="text-xs text-gray-500 mt-1">{t("vocabulary.loadingDesc", "Sincronizando seus termos salvos de forma segura.")}</p>
        </div>
      ) : vocab.length === 0 ? (
        <div className="py-12 text-center text-gray-400 flex flex-col items-center justify-center bg-white rounded-2xl border border-gray-100 shadow-sm">
          <Sparkles className="w-12 h-12 mb-3 stroke-[1.2] text-gray-300" />
          <h4 className="font-semibold text-gray-700 text-sm">{t("vocabulary.noTermsTitle", "Nenhum termo salvo ainda.")}</h4>
          <p className="text-xs text-gray-500 mt-1 max-w-sm">
            {t("vocabulary.noTermsDesc", "Use a opção de salvar termos selecionados durante os simulados para vê-los catalogados nesta área.")}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="bg-blue-50/50 border border-blue-100 p-4 rounded-2xl flex flex-wrap gap-4 items-center justify-between">
            <div className="flex items-center gap-3">
              <input 
                type="checkbox"
                checked={vocab.length > 0 && vocab.every(v => selectedIds.includes(v.id))}
                ref={el => {
                  if (el) {
                    const allSelected = vocab.length > 0 && vocab.every(v => selectedIds.includes(v.id));
                    const someSelected = vocab.some(v => selectedIds.includes(v.id));
                    el.indeterminate = someSelected && !allSelected;
                  }
                }}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedIds(vocab.map(v => v.id));
                  } else {
                    setSelectedIds([]);
                  }
                }}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
              />
              <span className="text-xs font-bold text-gray-700">
                {selectedIds.length > 0 
                  ? t("vocabulary.wordsSelectedCount", "{{count}} selecionada(s) de {{total}} palavras", { count: selectedIds.length, total: vocab.length })
                  : t("vocabulary.selectAllCount", "Selecionar todas ({{total}})", { total: vocab.length })
                }
              </span>
            </div>

            {selectedIds.length > 0 && (
              <Button
                variant="destructive"
                size="sm"
                disabled={isDeletingBulk}
                onClick={handleBulkDelete}
                className="rounded-xl font-bold text-xs h-9 px-4 shadow-sm transition-all duration-200"
              >
                {isDeletingBulk ? (
                  <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />
                ) : (
                  <Trash2 className="w-3.5 h-3.5 mr-2" />
                )}
                {t("vocabulary.deleteSelectedCount", "Excluir Selecionadas ({{count}})", { count: selectedIds.length })}
              </Button>
            )}
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {vocab.map((v: any) => {
              const isChecked = selectedIds.includes(v.id);
              return (
                <Card 
                  key={v.id} 
                  onClick={() => setSelectedWord(v)}
                  className={`border-none shadow-sm bg-white overflow-hidden flex flex-col justify-between hover:shadow-md transition-all duration-200 cursor-pointer ${
                    isChecked ? 'ring-2 ring-blue-500 bg-blue-50/10' : ''
                  }`}
                >
                  <div>
                    <div className="p-1 bg-blue-500" />
                    <CardHeader className="pt-6">
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3">
                          <input 
                            type="checkbox"
                            checked={isChecked}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedIds(prev => [...prev, v.id]);
                              } else {
                                setSelectedIds(prev => prev.filter(id => id !== v.id));
                              }
                            }}
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer shrink-0"
                          />
                          <span className="text-xs font-bold uppercase text-blue-600 bg-blue-50 border border-blue-100 px-2.5 py-0.5 rounded-md">
                            {getCategoryLabel(v.category)}
                          </span>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(v.id);
                          }}
                          className="h-8 w-8 text-gray-400 hover:text-red-500 rounded-lg hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      <CardTitle className="text-xl font-bold text-gray-900 pt-2 font-serif">{v.term}</CardTitle>
                      <CardDescription className="text-sm font-medium text-gray-600">{v.meaning}</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* Usage Context block */}
                      <div className="p-3 bg-gray-50 rounded-xl border border-gray-100 flex gap-2">
                        <Quote className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                        <p className="text-xs text-gray-500 leading-relaxed italic">"{v.context}"</p>
                      </div>

                      {/* Subtag pills */}
                      <div className="flex flex-wrap gap-1.5 pt-2">
                        {(v.tags || []).map((tag: string) => (
                          <Badge key={tag} variant="secondary" className="text-[10px] bg-gray-100 text-gray-600 border-none hover:bg-gray-100">
                            #{tag}
                          </Badge>
                        ))}
                      </div>
                    </CardContent>
                  </div>

                  <CardFooter className="pt-0 pb-6 px-6">
                    <Button 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCreateFlashcard(v);
                      }}
                      disabled={creatingFlashcardId === v.id || createdFlashcards[v.id]}
                      className="w-full bg-primary/10 text-primary hover:bg-primary/20 hover:text-primary rounded-xl text-xs py-4.5 font-bold border border-primary/20"
                    >
                      {creatingFlashcardId === v.id ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          {t("vocabulary.creating", "Criando...")}
                        </>
                      ) : createdFlashcards[v.id] ? (
                        <>
                          <Check className="w-4 h-4 mr-2 text-emerald-600" />
                          <span className="text-emerald-700">{t("vocabulary.flashcardCreated", "Flashcard Criado")}</span>
                        </>
                      ) : (
                        <>
                          <PlusCircle className="w-4 h-4 mr-2" />
                          {t("vocabulary.createFlashcard", "Criar Flashcard")}
                        </>
                      )}
                    </Button>
                  </CardFooter>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Manual Word Add Dialog Modal */}
      {isAddOpen && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4 animate-in fade-in duration-200">
          <Card className="max-w-md w-full bg-white border-none shadow-2xl rounded-2xl overflow-hidden animate-in zoom-in-95 duration-200">
            <CardHeader className="bg-gray-50 border-b border-gray-100 pb-4 flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-base font-bold text-gray-900">{t("vocabulary.addNewTerm", "Novo Termo de Vocabulário")}</CardTitle>
                <CardDescription className="text-xs">{t("vocabulary.addNewTermDesc", "Registre uma nova expressão ou palavra para estudo.")}</CardDescription>
              </div>
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => setIsAddOpen(false)}
                className="h-8 w-8 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-4.5 h-4.5" />
              </Button>
            </CardHeader>
            <form onSubmit={handleAddSubmit}>
              <CardContent className="p-6 space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="term" className="text-xs font-bold text-gray-500">{t("vocabulary.frenchTerm", "Termo ou Palavra (Francês) *")}</Label>
                  <Textarea 
                    id="term" 
                    value={newTerm}
                    onChange={(e) => setNewTerm(e.target.value)}
                    required
                    placeholder={t("vocabulary.termPlaceholder", "Ex: Néanmoins, Atout...")} 
                    className="bg-gray-50 border-none rounded-xl py-3 min-h-[80px]"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="meaning" className="text-xs font-bold text-gray-500">{t("vocabulary.translationMeaning", "Significado / Tradução *")}</Label>
                  <Input 
                    id="meaning" 
                    value={newMeaning}
                    onChange={(e) => setNewMeaning(e.target.value)}
                    required
                    placeholder={t("vocabulary.meaningPlaceholder", "Ex: No entanto, Vantagem...")} 
                    className="bg-gray-50 border-none rounded-xl py-5"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="context" className="text-xs font-bold text-gray-500">{t("vocabulary.contextSentence", "Contexto de Uso (Opcional)")}</Label>
                  <Input 
                    id="context" 
                    value={newContext}
                    onChange={(e) => setNewContext(e.target.value)}
                    placeholder={t("vocabulary.contextPlaceholder", "Ex: C'est un atout majeur...")} 
                    className="bg-gray-50 border-none rounded-xl py-5"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="category" className="text-xs font-bold text-gray-500">{t("vocabulary.category", "Categoria")}</Label>
                    <select
                      id="category"
                      value={newCategory}
                      onChange={(e) => setNewCategory(e.target.value)}
                      className="w-full bg-gray-50 text-gray-800 text-xs font-medium rounded-xl border-none p-3 focus:ring-1 focus:ring-primary h-[42px] cursor-pointer"
                    >
                      <option value="Conectores">{t("vocabulary.categories.connectors", "Conectores")}</option>
                      <option value="Substantivo">{t("vocabulary.categories.noun", "Substantivo")}</option>
                      <option value="Verbo">{t("vocabulary.categories.verb", "Verbo")}</option>
                      <option value="Adjetivo">{t("vocabulary.categories.adjective", "Adjetivo")}</option>
                      <option value="Idiomatismo">{t("vocabulary.categories.idiom", "Idiomatismo")}</option>
                    </select>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="tags" className="text-xs font-bold text-gray-500">{t("vocabulary.tagsLabel", "Etiquetas (tags, sep. por vírgula)")}</Label>
                    <Input 
                      id="tags" 
                      value={newTagsStr}
                      onChange={(e) => setNewTagsStr(e.target.value)}
                      placeholder={t("vocabulary.tagsPlaceholder", "Ex: avançado, escrita")} 
                      className="bg-gray-50 border-none rounded-xl py-5"
                    />
                  </div>
                </div>
              </CardContent>
              <CardFooter className="bg-gray-50/50 border-t border-gray-100 p-4 flex gap-2 justify-end">
                <Button 
                  type="button"
                  variant="outline"
                  onClick={() => setIsAddOpen(false)}
                  className="rounded-xl border-gray-100 font-semibold text-xs"
                >
                  {t("common.cancel", "Cancelar")}
                </Button>
                <Button 
                  type="submit"
                  disabled={isSaving}
                  className="bg-primary hover:bg-primary/95 text-white font-bold rounded-xl text-xs"
                >
                  {isSaving ? t("vocabulary.adding", "Adicionando...") : t("vocabulary.addTermButton", "Adicionar Termo")}
                </Button>
              </CardFooter>
            </form>
          </Card>
        </div>
      )}

      {/* DETAILED WORD VIEW MODAL */}
      <ContextModal
        isOpen={!!selectedWord}
        onClose={() => setSelectedWord(null)}
        title={t("vocabulary.wordDetails", "Detalhes do Vocabulário")}
        description={t("vocabulary.wordDetailsDesc", "Termo, tradução e exemplos em contexto real.")}
        size="md"
      >
        {selectedWord && (
          <div className="space-y-5">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold uppercase text-blue-600 bg-blue-50 border border-blue-100 px-2.5 py-0.5 rounded-md">
                {getCategoryLabel(selectedWord.category)}
              </span>
            </div>

            <div>
              <h3 className="text-2xl font-bold text-gray-900 font-serif mb-1">{selectedWord.term}</h3>
              <p className="text-base font-semibold text-gray-700">{selectedWord.meaning}</p>
            </div>

            <div className="p-4 bg-gray-50 rounded-xl border border-gray-100 space-y-2">
              <div className="flex items-center gap-1.5 text-gray-400">
                <Quote className="w-4 h-4" />
                <span className="text-[10px] font-bold uppercase tracking-wider">{t("vocabulary.exampleContext", "Frase de Exemplo / Contexto")}</span>
              </div>
              <p className="text-xs text-gray-600 italic leading-relaxed font-medium">"{selectedWord.context}"</p>
            </div>

            {selectedWord.tags && selectedWord.tags.length > 0 && (
              <div className="space-y-1.5">
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">{t("vocabulary.tagsLabel", "Etiquetas")}</span>
                <div className="flex flex-wrap gap-1.5">
                  {selectedWord.tags.map((tag: string) => (
                    <Badge key={tag} variant="secondary" className="text-[10px] bg-gray-100 text-gray-600 border-none hover:bg-gray-100">
                      #{tag}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            <div className="pt-4 border-t border-gray-100 flex gap-2 justify-end">
              <Button 
                variant="outline"
                onClick={() => setSelectedWord(null)}
                className="rounded-xl border-gray-100 font-semibold text-xs"
              >
                {t("vocabulary.close", "Fechar")}
              </Button>
              {createdFlashcards[selectedWord.id] ? (
                <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 font-bold text-xs px-3.5 py-1.5 rounded-xl self-center">
                  {t("vocabulary.flashcardCreated", "Flashcard Criado")}
                </Badge>
              ) : (
                <Button 
                  onClick={async () => {
                    await handleCreateFlashcard(selectedWord);
                    setSelectedWord(null);
                  }}
                  disabled={creatingFlashcardId === selectedWord.id}
                  className="bg-primary hover:bg-primary/95 text-white font-bold rounded-xl text-xs"
                >
                  {creatingFlashcardId === selectedWord.id ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {t("vocabulary.creating", "Criando...")}
                    </>
                  ) : (
                    <>
                      <PlusCircle className="w-4 h-4 mr-2" />
                      {t("vocabulary.createFlashcard", "Criar Flashcard")}
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        )}
      </ContextModal>
    </div>
  );
}
